/* assets/cart-recommendations.js
   Cart drawer recommendations carousel (related products)
   - Fetches related products from Shopify recommendations endpoint (Search & Discovery related logic)
   - 1 visible at a time, 8–10 total
   - Prev/Next arrows + swipe/drag
   - AJAX add to cart (no redirect)
   - Forces real-time cart drawer refresh + cart bubble refresh (no page reload)
*/

(() => {
  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this._index = 0;
      this._items = [];
      this._loadedFor = null;
      this._unsub = null;

      this._onClick = this._onClick.bind(this);
      this._onCartUpdate = this._onCartUpdate.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-cd-viewport]');
      this.track = this.querySelector('[data-cd-track]');
      this.prevBtn = this.querySelector('[data-cd-prev]');
      this.nextBtn = this.querySelector('[data-cd-next]');

      if (!this.viewport || !this.track) return;

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this._index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this._index + 1));

      this.addEventListener('click', this._onClick);
      this._bindSwipe();

      this.load();

      // Optional: listen to Dawn pubsub cart updates if available
      if (window.subscribe && window.PUB_SUB_EVENTS?.cartUpdate) {
        this._unsub = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, this._onCartUpdate);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this._unsub) this._unsub();
    }

    async _onCartUpdate(event) {
      const cartData = event?.cartData;
      const variantId = event?.variantId;

      // Seed with last-updated item if possible, else first cart item
      let seed = cartData?.items?.[0]?.product_id;

      if (variantId && Array.isArray(cartData?.items)) {
        const match = cartData.items.find((it) => String(it.variant_id) === String(variantId));
        if (match?.product_id) seed = match.product_id;
      }

      if (seed) {
        this.dataset.productId = String(seed);
        await this.load(true);
      }
    }

    async load(force = false) {
      const productId = this.dataset.productId;
      const limit = parseInt(this.dataset.limit || '10', 10);

      if (!productId) return;

      if (!force && this._loadedFor === productId) return;
      this._loadedFor = productId;
      this._index = 0;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const url =
          `${root}recommendations/products.json` +
          `?product_id=${encodeURIComponent(productId)}` +
          `&limit=${encodeURIComponent(limit)}` +
          `&intent=related`;

        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`Recommendations fetch failed: ${res.status}`);

        const data = await res.json();
        const products = data?.products || [];

        if (!products.length) {
          this.style.display = 'none';
          return;
        }

        this.style.display = '';
        this._items = products;

        this.track.innerHTML = products.map((p) => this._renderSlide(p)).join('');
        this.goTo(0);
        this._updateNav();
      } catch (e) {
        console.error('[cart-recommendations] load failed', e);
        this.style.display = 'none';
      }
    }

    _renderSlide(p) {
      const title = this._esc(p.title);
      const url = p.url || '#';
      const img = p.featured_image ? `<img src="${p.featured_image}" alt="${title}" loading="lazy">` : '';
      const price = typeof p.price === 'number' ? this._formatMoney(p.price) : '';
      const variantId = p?.variants?.[0]?.id;

      return `
        <div class="cd-rec__slide">
          <div class="cd-rec__item">
            <a class="cd-rec__img" href="${url}" aria-label="${title}">${img}</a>
            <div class="cd-rec__meta">
              <a class="cd-rec__name cart-item__name h4 break" href="${url}">${title}</a>
              <p class="cd-rec__price product-option">${price}</p>
              ${
                variantId
                  ? `<button type="button" class="button button--primary cd-rec__add" data-cd-add data-variant-id="${variantId}">
                       Add
                     </button>`
                  : ``
              }
            </div>
          </div>
        </div>
      `;
    }

    goTo(i) {
      const max = this._items.length - 1;
      if (max < 0) return;

      // Loop always
      if (i < 0) i = max;
      if (i > max) i = 0;

      this._index = i;
      this.track.style.transform = `translateX(-${this._index * 100}%)`;
      this._updateNav();
    }

    _updateNav() {
      const max = this._items.length - 1;
      const multi = max >= 1;

      if (this.prevBtn) this.prevBtn.disabled = !multi;
      if (this.nextBtn) this.nextBtn.disabled = !multi;
    }

    async _onClick(e) {
      const btn = e.target.closest('[data-cd-add]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      try {
        // 1) Add via AJAX (no redirect)
        await this._ajaxAddToCart(variantId, 1);

        // 2) Force-refresh cart drawer + bubble immediately (no reload)
        await this._refreshCartUI();

        // 3) Bonus: publish cartUpdate event so other listeners can react
        await this._publishCartUpdate(variantId);

        btn.textContent = 'Added';
        setTimeout(() => (btn.textContent = original), 900);
      } catch (err) {
        console.error('[cart-recommendations] add failed', err);
        btn.textContent = 'Error';
        setTimeout(() => (btn.textContent = original), 1200);
      } finally {
        btn.disabled = false;
      }
    }

    async _ajaxAddToCart(variantId, qty) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: Number(variantId), quantity: Number(qty || 1) }),
      });

      if (!res.ok) {
        let msg = `Add failed (${res.status})`;
        try {
          const err = await res.json();
          if (err?.description) msg = err.description;
        } catch (_) {}
        throw new Error(msg);
      }

      return res.json();
    }

    async _refreshCartUI() {
      // Refresh cart drawer items + footer by re-rendering the cart-drawer section
      // This matches your cart.js onCartUpdate() logic
      const cartUrl = window.routes?.cart_url || '/cart';

      // 1) Refresh cart drawer section HTML
      const drawerSectionUrl = `${cartUrl}?section_id=cart-drawer`;
      const responseText = await fetch(drawerSectionUrl, { credentials: 'same-origin' }).then((r) => r.text());
      const doc = new DOMParser().parseFromString(responseText, 'text/html');

      // Replace drawer items + footer if present
      const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
      selectors.forEach((sel) => {
        const target = document.querySelector(sel);
        const source = doc.querySelector(sel);
        if (target && source) target.replaceWith(source);
      });

      // Keep drawer open (some themes may reset state)
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }

      // Re-init countdown timer if it was just injected
      if (drawer && window.WbsCountdownTimer) {
        window.WbsCountdownTimer.init(drawer);
      }

      // 2) Refresh cart bubble (most Dawn-like themes)
      // Try a few common mounts safely.
      await this._refreshCartBubble(cartUrl);
    }

    async _refreshCartBubble(cartUrl) {
      try {
        const bubbleSectionUrl = `${cartUrl}?section_id=cart-icon-bubble`;
        const bubbleText = await fetch(bubbleSectionUrl, { credentials: 'same-origin' }).then((r) => r.text());
        const bubbleDoc = new DOMParser().parseFromString(bubbleText, 'text/html');

        // Dawn returns a .shopify-section wrapper for that section
        const newSection = bubbleDoc.querySelector('.shopify-section');
        if (!newSection) return;

        // Common mounts
        const mountById = document.getElementById('cart-icon-bubble');
        if (mountById) {
          const existing = mountById.querySelector('.shopify-section');
          if (existing) existing.replaceWith(newSection);
          else mountById.innerHTML = newSection.outerHTML;
          return;
        }

        // Another common pattern: header contains the bubble section
        const existingSection = document.querySelector('header .shopify-section#shopify-section-cart-icon-bubble');
        if (existingSection) {
          existingSection.replaceWith(newSection);
        }
      } catch (e) {
        // Bubble refresh is non-critical
      }
    }

    async _publishCartUpdate(variantId) {
      try {
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        const cartJson = await cartRes.json();

        const pub = (typeof publish === 'function') ? publish : window.publish;
        const evt = window.PUB_SUB_EVENTS?.cartUpdate || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.cartUpdate : null);

        if (pub && evt) {
          pub(evt, { source: 'cart-recommendations', cartData: cartJson, variantId });
        }
      } catch (_) {
        // ignore
      }
    }

    _bindSwipe() {
      if (!this.viewport) return;

      let startX = 0;
      let currentX = 0;
      let dragging = false;

      const start = (x) => {
        dragging = true;
        startX = x;
        currentX = x;
      };

      const move = (x) => {
        if (!dragging) return;
        currentX = x;
      };

      const end = () => {
        if (!dragging) return;
        dragging = false;

        const dx = currentX - startX;
        if (Math.abs(dx) < 35) return;
        if (dx < 0) this.goTo(this._index + 1);
        else this.goTo(this._index - 1);
      };

      this.viewport.addEventListener('touchstart', (e) => start(e.touches[0].clientX), { passive: true });
      this.viewport.addEventListener('touchmove', (e) => move(e.touches[0].clientX), { passive: true });
      this.viewport.addEventListener('touchend', end);

      this.viewport.addEventListener('mousedown', (e) => {
        e.preventDefault();
        start(e.clientX);
      });
      window.addEventListener('mousemove', (e) => move(e.clientX));
      window.addEventListener('mouseup', end);
    }

    _formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
      }
      return (cents / 100).toFixed(2);
    }

    _esc(str) {
      return String(str || '').replace(/[&<>"']/g, (m) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[m]));
    }
  }

  if (!customElements.get('cart-recommendations')) {
    customElements.define('cart-recommendations', CartRecommendations);
  }
})();